import { Component, EventEmitter, Input, Output } from '@angular/core';
import { catchError, EMPTY, Observable, of, switchMap, tap } from 'rxjs';
import {
  CartridgeVariantAlternativeOwnerResponseDto,
  CartridgeVariantCurrentOwnersResponseDto,
  DeactivateOrganizationMembershipsResponseDto,
} from '@markant-platform/shared/user/model';
import { AuthenticatedUserService, UserService } from '@markant-platform/shared/user/data-access';

import { SnackbarService } from '@markant-platform/shared/snackbar/feature';
import { ErrorCode, ErrorResponse } from '@markant-platform/shared/error-model';

@Component({
  selector: 'markant-platform-organization-member-remove-user-dialog',
  templateUrl: './organization-member-remove-user-dialog.component.html',
  styleUrls: ['./organization-member-remove-user-dialog.component.scss'],
})
export class OrganizationMemberRemoveUserDialogComponent {
  @Input({ required: true }) organizationId!: string;

  @Input({ required: true }) users!: { userId: string; version: number }[];

  @Input() set open(open: boolean) {
    if (!open) {
      return;
    }

    if (
      this.authenticatedUserService.userId &&
      this.users.map(user => user.userId).includes(this.authenticatedUserService.userId)
    ) {
      this.isWarningUserDialogOpen = true;
    } else {
      this.removeEmployeeDialogOpen = true;
    }
  }

  @Output() closeChanged = new EventEmitter<void>();
  @Output() removalCompleted = new EventEmitter<boolean>();

  isLoading = false;
  removeEmployeeDialogOpen = false;
  owners: CartridgeVariantCurrentOwnersResponseDto[] = [];
  alternativeOwners: CartridgeVariantAlternativeOwnerResponseDto[] = [];
  isAssignAdminForServiceBookingResponsibilityOpen = false;
  isWarningUserDialogOpen = false;
  noOtherOrgaAdminDialogOpen = false;

  constructor(
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly userService: UserService,
    private readonly snackbarService: SnackbarService
  ) {}

  removeUsers(): void {
    if (!this.organizationId || this.users.length === 0) {
      this.handleError();
      return;
    }

    this.isLoading = true;
    this.userService
      .checkAlternativeCartridgeVariantOwners(
        this.organizationId,
        this.users.map(user => user.userId)
      )
      .pipe(
        switchMap(alternativeCartridgeVariantOwners => {
          if (alternativeCartridgeVariantOwners?.owners?.length) {
            this.owners = alternativeCartridgeVariantOwners.owners;
            this.alternativeOwners = alternativeCartridgeVariantOwners.alternativeOwners;
            this.isAssignAdminForServiceBookingResponsibilityOpen = true;
            this.isLoading = false;
            return of(void 0);
          } else {
            return this.deactivateOrganizationMembershipAndHandleResponse();
          }
        }),
        catchError(err => this.handleError(err?.error?.errorCode))
      )
      .subscribe();
  }

  removeMembersAndAssignNewOwners(
    newOwners?: {
      userId: string;
      cartridgeVariantId: string;
    }[]
  ): void {
    this.deactivateOrganizationMembershipAndHandleResponse(newOwners)
      .pipe(catchError(err => this.handleError(err?.error?.errorCode)))
      .subscribe();
  }

  private deactivateOrganizationMembershipAndHandleResponse(
    newOwners?: {
      userId: string;
      cartridgeVariantId: string;
    }[]
  ): Observable<DeactivateOrganizationMembershipsResponseDto> {
    this.isLoading = true;
    return this.userService.deactivateOrganizationMembership(this.organizationId, this.users, newOwners).pipe(
      catchError(err => this.handleError(err?.error?.errorCode)),
      tap(res => {
        this.isLoading = false;
        this.handleRemoveResponse(res);
      })
    );
  }

  private handleError(errorCode?: string): Observable<never> {
    this.isLoading = false;
    this.removeEmployeeDialogOpen = false;
    this.isAssignAdminForServiceBookingResponsibilityOpen = false;
    let errorMessage = 'organization.users.remove.error';
    switch (errorCode) {
      case ErrorCode.USER_APPOINTED_TO_FURTHER_QUALIFICATION:
        errorMessage = 'organization.users.remove.errors.USE-003';
        break;
    }
    this.snackbarService.showErrorSnackbarWithTransloco(errorMessage);
    this.closeChanged.emit();
    return EMPTY;
  }

  private handleRemoveResponse(res: DeactivateOrganizationMembershipsResponseDto): void {
    if (!res.withFailure) {
      this.snackbarService.showSuccessSnackbarWithTransloco('organization.users.remove.success', {});
    }

    if (res.withFailure && res.deactivatedUserIds.length === 0) {
      const failures: ErrorResponse[] =
        (res.failures as unknown) instanceof Map ? Array.from(res.failures.values()) : Object.values(res.failures);
      // TODO: update this once we have custom error codes on the BE
      if (failures.some(failure => failure.errorCode === ErrorCode.ORG_MEMBERSHIP_CANNOT_DELETE_LAST_ADMIN)) {
        this.noOtherOrgaAdminDialogOpen = true;
      } else if (
        failures.some(failure => failure.errorCode === ErrorCode.ORG_MEMBERSHIP_CANNOT_DELETE_LAST_ACTIVE_MEMBER)
      ) {
        this.snackbarService.showErrorSnackbarWithTransloco('organization.users.remove.errors.USE-047');
      } else if (failures.some(failure => failure.errorCode === ErrorCode.ORG_MEMBERSHIP_CANNOT_DELETE_LAST_MEMBER)) {
        this.snackbarService.showErrorSnackbarWithTransloco('organization.users.remove.errors.USE-048');
      } else if (failures.some(failure => failure.errorCode === ErrorCode.USER_APPOINTED_TO_FURTHER_QUALIFICATION)) {
        this.snackbarService.showErrorSnackbarWithTransloco('organization.users.remove.errors.USE-003');
      } else {
        this.snackbarService.showErrorSnackbarWithTransloco('organization.users.remove.error');
      }
    }

    if (res.withFailure && res.deactivatedUserIds.length !== 0) {
      const failedUsers: [string, ErrorResponse][] =
        (res.failures as unknown) instanceof Map ? Array.from(res.failures.entries()) : Object.entries(res.failures);
      this.snackbarService.showErrorSnackbarWithTransloco('organization.users.remove.errors.partial-deleted', {
        notDeleted: failedUsers.map(value => `${value[0]} (${value[1].message})`).join(', '),
      });
    }

    if (!this.noOtherOrgaAdminDialogOpen) {
      this.closeChanged.emit();
    }
    this.removalCompleted.emit(!res.withFailure);
  }

  closeDialog(): void {
    this.isAssignAdminForServiceBookingResponsibilityOpen = false;
    this.noOtherOrgaAdminDialogOpen = false;
    this.closeChanged.emit();
  }

  closeWarningUserDialog(): void {
    this.isWarningUserDialogOpen = false;
    this.closeChanged.emit();
  }
}
